import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, ExternalLink, CheckCircle2, Check, X, Loader2, GraduationCap, Sparkles, AlertCircle, UserCheck, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

type ProposalType = "new_class_proposal" | "existing_class_application";

type Proposal = {
  id: string;
  created_at: string;
  full_name: string;
  email: string;
  phone: string;
  proposed_class_name: string;
  class_description: string;
  age_range: string;
  experience: string;
  preferred_days: string;
  preferred_time: string;
  preferred_length: string;
  semester_price_cents: number;
  requires_supplies: boolean;
  supplies_details: string | null;
  requires_special_space: boolean;
  special_space_details: string | null;
  ack_initials: string;
  status: string;
  review_status: "New" | "Reviewed";
  internal_notes: string | null;
  proposal_type: ProposalType | null;
  target_class_id: string | null;
  target_class_slug: string | null;
  target_class_title_snapshot: string | null;
  target_class_status_snapshot: string | null;
  target_class_instructor_snapshot: string | null;
  why_this_class: string | null;
  earliest_start_date: string | null;
  resume_url: string | null;
  certifications: string | null;
  references_text: string | null;
  linked_instructor_id: string | null;
  accepted_at: string | null;

};

const STATUSES = ["New", "Reviewing", "Contacted", "Interview Scheduled", "Approved", "Declined", "Archived"];

const statusColor = (s: string) => {
  switch (s) {
    case "New": return "bg-blue-100 text-blue-800 border-blue-300";
    case "Reviewing": return "bg-amber-100 text-amber-800 border-amber-300";
    case "Contacted": return "bg-purple-100 text-purple-800 border-purple-300";
    case "Interview Scheduled": return "bg-indigo-100 text-indigo-800 border-indigo-300";
    case "Approved": return "bg-green-100 text-green-800 border-green-300";
    case "Declined": return "bg-red-100 text-red-800 border-red-300";
    default: return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

const fmtPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

interface Props {
  userId: string | null | undefined;
  focusId?: string | null;
}

type MatchCandidate = {
  id: string;
  full_name: string;
  preferred_name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  confidence: number;
  reason: string;
};

type ApproveOutcome = {
  instructorId: string;
  instructorName: string;
  linkedExisting: boolean;
  alreadyApproved: boolean;
};

const InstructorProposals = ({ userId, focusId }: Props) => {
  const [rows, setRows] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [open, setOpen] = useState<Proposal | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [acceptTarget, setAcceptTarget] = useState<Proposal | null>(null);
  const [declineTarget, setDeclineTarget] = useState<Proposal | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ classId: string; thumbnailUrl: string | null } | null>(null);
  // Approve & Add to Instructors (directory-only, idempotent)
  const [approveTarget, setApproveTarget] = useState<Proposal | null>(null);
  const [candidates, setCandidates] = useState<MatchCandidate[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [approveOutcome, setApproveOutcome] = useState<ApproveOutcome | null>(null);
  const [sendingOnboarding, setSendingOnboarding] = useState(false);
  const navigate = useNavigate();


  const fetchRows = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("instructor_proposals")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error) setRows((data ?? []) as Proposal[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    const ch = supabase.channel("admin-instructor-proposals")
      .on("postgres_changes", { event: "*", schema: "public", table: "instructor_proposals" }, () => {
        fetchRows();
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  useEffect(() => {
    if (!focusId) return;
    const target = rows.find((r) => r.id === focusId);
    if (target) {
      setOpen(target);
      setNotesDraft(target.internal_notes ?? "");
    }
  }, [focusId, rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      return [r.full_name, r.email, r.proposed_class_name].some((v) =>
        (v ?? "").toLowerCase().includes(q));
    });
  }, [rows, search, statusFilter]);

  const setStatus = async (id: string, status: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    const { error } = await supabase
      .from("instructor_proposals")
      .update({ status })
      .eq("id", id);
    if (error) { toast.error(error.message); fetchRows(); }
    else toast.success(`Status set to ${status}`);
  };

  const markReviewed = async (id: string) => {
    const patch = { review_status: "Reviewed" as const, reviewed_at: new Date().toISOString(), reviewed_by: userId ?? null };
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("instructor_proposals").update(patch).eq("id", id);
    if (error) { toast.error(error.message); fetchRows(); }
  };

  const saveNotes = async () => {
    if (!open) return;
    const { error } = await supabase
      .from("instructor_proposals")
      .update({ internal_notes: notesDraft })
      .eq("id", open.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Notes saved");
      setRows((prev) => prev.map((r) => (r.id === open.id ? { ...r, internal_notes: notesDraft } : r)));
    }
  };

  // ---- Approve & Add to Instructors ------------------------------------
  // Directory-only approval. Never creates a class, an assignment, payroll,
  // or marks onboarding complete. The proposal → instructor link is the
  // source of truth, so repeated clicks reuse the same instructor record.
  const openApprove = async (r: Proposal) => {
    setApproveTarget(r);
    setCandidates(null);
    setChecking(true);
    try {
      const { data, error } = await supabase.rpc("admin_match_instructor_for_proposal", {
        _proposal_id: r.id,
      });
      if (error) throw error;
      const payload = data as any;
      if (payload?.alreadyLinked && payload?.instructorId) {
        setApproveTarget(null);
        setApproveOutcome({
          instructorId: payload.instructorId,
          instructorName: r.full_name,
          linkedExisting: true,
          alreadyApproved: true,
        });
        return;
      }
      setCandidates((payload?.candidates ?? []) as MatchCandidate[]);
    } catch (e: any) {
      setApproveTarget(null);
      toast.error("I couldn't check the Instructors Directory just now. Nothing changed — try again in a moment.");
      console.error("[proposal match]", e);
    } finally {
      setChecking(false);
    }
  };

  const runApprove = async (linkInstructorId: string | null) => {
    if (!approveTarget) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc("admin_approve_proposal_to_instructor", {
        _proposal_id: approveTarget.id,
        _link_instructor_id: linkInstructorId,
        _confirm_new: linkInstructorId === null,
      });
      if (error) throw error;
      const payload = data as any;
      if (payload?.needsDecision) {
        setCandidates((payload.candidates ?? []) as MatchCandidate[]);
        return;
      }
      setApproveOutcome({
        instructorId: payload.instructorId,
        instructorName: payload.instructorName ?? approveTarget.full_name,
        linkedExisting: payload.linkedExisting === true,
        alreadyApproved: payload.alreadyApproved === true,
      });
      setApproveTarget(null);
      setCandidates(null);
      fetchRows();
    } catch (e: any) {
      const raw = `${e?.message ?? ""}`;
      toast.error(
        /forbidden/i.test(raw)
          ? "Only administrators can approve proposals."
          : "I couldn't complete the approval. Nothing was changed — please try again.",
      );
      console.error("[proposal approve]", e);
    } finally {
      setBusy(false);
    }
  };

  const sendOnboarding = async (instructorId: string, name: string) => {
    setSendingOnboarding(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-onboarding-packet", {
        body: { instructorId },
      });
      if (error) throw error;
      const code = (data as any)?.error;
      if (code === "requires_approval" || code === "requires_reapproval") {
        toast.error("Preview and approve this onboarding packet on the Instructors page before sending.");
        return;
      }
      if (code) throw new Error(String(code));

      toast.success(`Onboarding request sent to ${name}`, {
        description: "They'll get a secure link for only the items still missing.",
      });
    } catch (e: any) {
      toast.error("I couldn't send the onboarding request. You can send it from the Instructors page instead.");
      console.error("[send onboarding]", e);
    } finally {
      setSendingOnboarding(false);
    }
  };

  const viewInstructor = (name: string) => {
    setApproveOutcome(null);
    navigate(`/admin/instructors?tab=directory&q=${encodeURIComponent(name)}`);
  };

  const runAccept = async () => {

    if (!acceptTarget) return;
    setBusy(true);
    try {
      // Single atomic server-side workflow handles BOTH new-class proposals and
      // existing-class applications (no duplicate class is ever created).
      const { data, error } = await supabase.functions.invoke("accept-instructor-proposal", {
        body: { proposalId: acceptTarget.id },
      });

      const payload = data as any;
      if (error && !payload?.ok) {
        // Surface the structured admin-facing message when the function returned one.
        let message = "";
        try {
          const ctx: any = (error as any)?.context;
          const parsed = ctx && typeof ctx.json === "function" ? await ctx.json() : null;
          message = parsed?.message || "";
        } catch { /* fall through to generic */ }
        throw new Error(message || (error as any)?.message || "");
      }
      if (payload && payload.ok === false) throw new Error(payload.message || "");

      if (payload?.warningMessage) toast.warning(payload.warningMessage);
      else if (payload?.isExistingClass) toast.success("Application approved and linked to the existing class.");
      else toast.success("Draft class created successfully");

      setResult({ classId: payload?.classId ?? null, thumbnailUrl: payload?.thumbnailUrl ?? null });
      setAcceptTarget(null);
      fetchRows();
    } catch (e: any) {
      console.error("[accept proposal]", e);
      toast.error(
        e?.message?.trim()
          ? e.message
          : "We couldn't save the acceptance. Nothing was changed — please retry.",
      );
    } finally {
      setBusy(false);
    }
  };


  const runDecline = async () => {
    if (!declineTarget) return;
    setBusy(true);
    try {
      const { error } = await supabase.functions.invoke("decline-instructor-proposal", {
        body: { proposalId: declineTarget.id, reason: declineReason || null },
      });
      if (error) throw error;
      toast.success("Proposal declined");
      setDeclineTarget(null);
      setDeclineReason("");
      fetchRows();
    } catch (e: any) {
      toast.error("Couldn't decline the proposal. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase">Total</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{rows.length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase">New</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{rows.filter(r => r.review_status === "New").length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase">In Progress</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{rows.filter(r => ["Reviewing","Contacted","Interview Scheduled"].includes(r.status)).length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase">Approved</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{rows.filter(r => r.status === "Approved").length}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Instructor Proposals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search name, email, or class..." className="pl-9"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="md:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Price / Student</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Review</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Checking new instructor proposals…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">You're all caught up — no proposals waiting.</TableCell></TableRow>
                ) : filtered.map((r) => {
                  const isExisting = r.proposal_type === "existing_class_application";
                  const classLabel = isExisting
                    ? (r.target_class_title_snapshot ?? r.proposed_class_name)
                    : r.proposed_class_name;
                  return (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-sm">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="font-medium">{r.full_name}</div>
                      <div className="text-xs text-muted-foreground">{r.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className={isExisting
                          ? "border-emerald-300 text-emerald-800 bg-emerald-50"
                          : "border-indigo-300 text-indigo-800 bg-indigo-50"}>
                          {isExisting ? (<><GraduationCap className="h-3 w-3 mr-1" />Existing class</>) : (<><Sparkles className="h-3 w-3 mr-1" />New class</>)}
                        </Badge>
                      </div>
                      <div className="font-medium mt-1">{classLabel}</div>
                      <div className="text-xs text-muted-foreground">{isExisting ? (r.target_class_instructor_snapshot ? `Currently taught by ${r.target_class_instructor_snapshot}` : "No instructor assigned") : r.age_range}</div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{isExisting ? <span className="text-muted-foreground">—</span> : fmtPrice(r.semester_price_cents)}</TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                        <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {r.review_status === "New"
                        ? <Badge className="bg-red-100 text-red-800 border-red-300">New</Badge>
                        : <Badge variant="outline" className="text-green-700 border-green-300">Reviewed</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        {r.linked_instructor_id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-700 border-emerald-300"
                            onClick={() => viewInstructor(r.full_name)}
                          >
                            <UserCheck className="h-3.5 w-3.5 mr-1" />In Instructors
                          </Button>
                        ) : (
                          r.status !== "Declined" && (
                            // ONE approval action. Existing-class applications add the
                            // person to the directory; new-class proposals also create the
                            // draft class shell. Neither creates a teaching assignment —
                            // that is done on the class from the Staffing Board.
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => (isExisting ? openApprove(r) : setAcceptTarget(r))}
                            >
                              <UserCheck className="h-3.5 w-3.5 mr-1" />Approve proposal
                            </Button>
                          )
                        )}
                        {r.status !== "Approved" && r.status !== "Declined" && (
                          <Button size="sm" variant="outline" onClick={() => { setDeclineTarget(r); setDeclineReason(""); }}>
                            <X className="h-3.5 w-3.5 mr-1" />Decline
                          </Button>
                        )}
                        {r.review_status === "New" && (
                          <Button size="sm" variant="outline" onClick={() => markReviewed(r.id)}>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </Button>
                        )}

                        <Button size="sm" variant="ghost" onClick={() => { setOpen(r); setNotesDraft(r.internal_notes ?? ""); }}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );})}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {open && (() => {
            const isExisting = open.proposal_type === "existing_class_application";
            return (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={isExisting
                    ? "border-emerald-300 text-emerald-800 bg-emerald-50"
                    : "border-indigo-300 text-indigo-800 bg-indigo-50"}>
                    {isExisting ? "Existing class application" : "New class proposal"}
                  </Badge>
                </div>
                <DialogTitle>{isExisting ? (open.target_class_title_snapshot ?? open.proposed_class_name) : open.proposed_class_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                {isExisting && (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 space-y-1">
                    <div className="font-medium text-emerald-900 flex items-center gap-1.5">
                      <GraduationCap className="h-4 w-4" />
                      Applying to teach an existing Silver Sage class
                    </div>
                    <div className="text-emerald-900/80 text-xs grid grid-cols-2 gap-x-4 gap-y-1 mt-1">
                      <div><span className="text-emerald-700/70">Class: </span>{open.target_class_title_snapshot ?? "—"}</div>
                      <div><span className="text-emerald-700/70">Status at submit: </span>{open.target_class_status_snapshot ?? "—"}</div>
                      <div className="col-span-2"><span className="text-emerald-700/70">Currently assigned instructor: </span>{open.target_class_instructor_snapshot ?? "None"}</div>
                    </div>
                    {open.target_class_id && (
                      <Button variant="link" size="sm" className="h-auto p-0 text-emerald-800"
                        onClick={() => navigate(`/admin/classes?focus=${encodeURIComponent(open.target_class_id!)}`)}>
                        Open class in Class Manager →
                      </Button>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Applicant</Label><p>{open.full_name}</p></div>
                  <div><Label>Email</Label><p>{open.email}</p></div>
                  <div><Label>Phone</Label><p>{open.phone}</p></div>
                  <div><Label>Submitted</Label><p>{new Date(open.created_at).toLocaleString()}</p></div>
                  {!isExisting && <div><Label>Age range</Label><p>{open.age_range}</p></div>}
                  {!isExisting && <div><Label>Price / student</Label><p>{fmtPrice(open.semester_price_cents)}</p></div>}
                </div>

                {isExisting ? (
                  <>
                    <div>
                      <Label>Why this class</Label>
                      <p className="whitespace-pre-wrap mt-1">{open.why_this_class ?? "—"}</p>
                    </div>
                    <div>
                      <Label>Experience & qualifications</Label>
                      <p className="whitespace-pre-wrap mt-1">{open.experience}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Preferred days</Label><p>{open.preferred_days}</p></div>
                      <div><Label>Earliest start</Label><p>{open.earliest_start_date ?? "—"}</p></div>
                    </div>
                    <div>
                      <Label>Resume link</Label>
                      <p>{open.resume_url ? <a className="underline text-primary" href={open.resume_url} target="_blank" rel="noreferrer">{open.resume_url}</a> : "—"}</p>
                    </div>
                    {open.certifications && (<div><Label>Certifications</Label><p className="whitespace-pre-wrap mt-1">{open.certifications}</p></div>)}
                    {open.references_text && (<div><Label>References</Label><p className="whitespace-pre-wrap mt-1">{open.references_text}</p></div>)}
                  </>
                ) : (
                  <>
                    <div>
                      <Label>Class description</Label>
                      <p className="whitespace-pre-wrap mt-1">{open.class_description}</p>
                    </div>
                    <div>
                      <Label>Experience</Label>
                      <p className="whitespace-pre-wrap mt-1">{open.experience}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>Days</Label><p>{open.preferred_days}</p></div>
                      <div><Label>Time</Label><p>{open.preferred_time}</p></div>
                      <div><Label>Length</Label><p>{open.preferred_length}</p></div>
                    </div>
                    <div>
                      <Label>Supplies needed</Label>
                      <p>{open.requires_supplies ? (open.supplies_details || "Yes") : "No"}</p>
                    </div>
                    <div>
                      <Label>Special space needed</Label>
                      <p>{open.requires_special_space ? (open.special_space_details || "Yes") : "No"}</p>
                    </div>
                  </>
                )}

                <div>
                  <Label>Acknowledgement initials</Label>
                  <p className="font-mono">{open.ack_initials}</p>
                </div>
                <div>
                  <Label htmlFor="notes">Internal notes (admin only)</Label>
                  <Textarea id="notes" rows={4} value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                {open.review_status === "New" && (
                  <Button variant="outline" onClick={() => markReviewed(open.id)}>
                    Mark reviewed
                  </Button>
                )}
                <Button onClick={saveNotes}>Save notes</Button>
              </DialogFooter>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Accept confirmation */}
      <Dialog open={!!acceptTarget} onOpenChange={(v) => !v && !busy && setAcceptTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{acceptTarget?.proposal_type === "existing_class_application" ? "Approve this application?" : "Accept this proposal?"}</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            {acceptTarget?.proposal_type === "existing_class_application" ? (
              <>
                <p>This will:</p>
                <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                  <li>Mark this application as <b>Approved</b></li>
                  <li>Leave <b>{acceptTarget?.target_class_title_snapshot ?? acceptTarget?.proposed_class_name}</b> exactly as it is</li>
                </ul>
                <p className="text-xs text-muted-foreground flex gap-1.5 items-start">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  No draft class will be created. After approving, add <b>{acceptTarget?.full_name}</b> from the Instructors page and assign them to this class when ready.
                </p>
              </>
            ) : (
              <>
                <p>This will:</p>
                <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                  <li>Create an instructor profile for <b>{acceptTarget?.full_name}</b></li>
                  <li>Create a draft class <b>{acceptTarget?.proposed_class_name}</b> in the Coming Soon section</li>
                  <li>Generate an AI thumbnail image</li>
                  <li>Mark this proposal as Approved</li>
                </ul>
                <p className="text-xs text-muted-foreground">The class will not accept registrations until you publish it.</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptTarget(null)} disabled={busy}>Cancel</Button>
            <Button onClick={runAccept} disabled={busy}>
              {busy
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Working…</>
                : (acceptTarget?.proposal_type === "existing_class_application" ? "Approve Application" : "Accept Proposal")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline confirmation */}
      <Dialog open={!!declineTarget} onOpenChange={(v) => !v && !busy && setDeclineTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline this proposal?</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>Optional internal reason (kept for records, not sent to the applicant):</p>
            <Textarea rows={4} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Reason (optional)" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineTarget(null)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" onClick={runDecline} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decline Proposal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success dialog */}
      <Dialog open={!!result} onOpenChange={(v) => !v && setResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Draft class created successfully</DialogTitle>
          </DialogHeader>
          {result?.thumbnailUrl ? (
            <img src={result.thumbnailUrl} alt="Generated thumbnail" className="w-full rounded-md border" />
          ) : (
            <p className="text-sm text-muted-foreground">
              Thumbnail generation failed or is queued — you can regenerate it from the class editor.
            </p>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setResult(null)}>Publish later</Button>
            <Button variant="outline" onClick={() => { navigate("/classes"); }}>View Coming Soon page</Button>
            <Button onClick={() => { const id = result?.classId; setResult(null); if (id) navigate(`/admin/classes?focus=${encodeURIComponent(id)}`); else navigate("/admin/classes"); }}>
              Edit draft class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve & Add to Instructors — duplicate check + confirmation */}
      <Dialog open={!!approveTarget} onOpenChange={(v) => !v && !busy && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {approveTarget?.full_name} to the Instructors Directory</DialogTitle>
          </DialogHeader>
          {checking ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking whether this person is already in the directory…
            </div>
          ) : candidates && candidates.length > 0 ? (
            <div className="text-sm space-y-3">
              <p>
                Someone who looks like this person is already in the directory. Linking keeps one
                record per person — creating a new one may cause a duplicate.
              </p>
              <ul className="space-y-2">
                {candidates.map((c) => (
                  <li key={c.id} className="rounded-md border p-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{c.preferred_name || c.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[c.email, c.phone, c.status].filter(Boolean).join(" · ")}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{c.reason}</div>
                    </div>
                    <Button size="sm" disabled={busy} onClick={() => runApprove(c.id)}>
                      Link to this person
                    </Button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground flex gap-1.5 items-start">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Approving only adds them to the directory. No class, assignment, payroll, or
                onboarding completion is created.
              </p>
            </div>
          ) : (
            <div className="text-sm space-y-2">
              <p>No existing instructor matches this person. This will:</p>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Create one instructor record for <b>{approveTarget?.full_name}</b></li>
                <li>Save their contact details and link this proposal to that record</li>
                <li>Keep the proposal and its history exactly as it is</li>
              </ul>
              <p className="text-xs text-muted-foreground">
                No class, assignment, payroll entry, or onboarding completion is created.
              </p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setApproveTarget(null)} disabled={busy || checking}>
              Cancel
            </Button>
            <Button onClick={() => runApprove(null)} disabled={busy || checking}>
              {busy
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Working…</>
                : candidates && candidates.length > 0
                  ? "None of these — create a new instructor"
                  : "Add to Instructors"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval success */}
      <Dialog open={!!approveOutcome} onOpenChange={(v) => !v && setApproveOutcome(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approveOutcome?.alreadyApproved
                ? `${approveOutcome?.instructorName} is already in the Instructors Directory`
                : approveOutcome?.linkedExisting
                  ? `Linked to the existing record for ${approveOutcome?.instructorName}`
                  : `${approveOutcome?.instructorName} was added to the Instructors Directory`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The proposal stays on this page with its full history. Nothing else was scheduled —
            assign classes or start onboarding whenever you're ready.
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setApproveOutcome(null)}>Done</Button>
            <Button
              variant="outline"
              disabled={sendingOnboarding}
              onClick={() => approveOutcome && sendOnboarding(approveOutcome.instructorId, approveOutcome.instructorName)}
            >
              {sendingOnboarding
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
                : <><Send className="h-4 w-4 mr-2" />Send onboarding request</>}
            </Button>
            <Button onClick={() => approveOutcome && viewInstructor(approveOutcome.instructorName)}>
              View instructor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
};

export default InstructorProposals;
